import { QueryStringInput } from '@hello-aria/query-string'
import {
    CreateReceivable,
    CreateReceivableGateway,
    Receivable,
    ReceivableGateway,
    UpdateReceivable,
    UpdateReceivableGateway
} from '../models/receivableModels'
import { batchFetch, resolveApiUrl } from '../utils/requestsUtils'
import { RequestResponse, requestService } from './requestServices'
import { isEmpty, isNil } from 'lodash'
import { downloadAttachments, uploadFileService } from './uploadServices'
import { UploadFileGateway } from '../models/uploadModels'
import { receivableInvoiceIsDueSoon, receivableInvoiceIsOverdue } from '../utils/receivableUtils'
import { trackEvent } from '../utils/analyticsUtils'

interface GetReceivablesParams {
    companyId: string
    query?: QueryStringInput
    hasAttachments?: boolean
    signal?: AbortSignal
}

export const listReceivablesService = batchFetch(
    async (params: GetReceivablesParams): Promise<RequestResponse<Receivable[]>> => {
        const url = resolveApiUrl('receivables.list', {
            variables: { companyId: params.companyId },
            query: {
                ...params.query,
                sorts: params.query?.sorts ?? [
                    {
                        path: 'createdAt',
                        direction: 'desc'
                    }
                ]
            }
        })

        const response = await requestService<ReceivableGateway[]>(
            url,
            params.signal
                ? {
                      signal: params.signal
                  }
                : undefined
        )

        if (!response?.body) {
            return { ...response, body: [] }
        }

        const mappedReceivables = await Promise.all(response.body.map((r) => mapReceivableService(r, false)))
        const sanitizedMappedReceivables = mappedReceivables.filter((r): r is Receivable => !isNil(r))

        return {
            ...response,
            body: sanitizedMappedReceivables
        }
    }
)

export const mapReceivableService = async (
    data: ReceivableGateway | undefined,
    hasAttachments = true
): Promise<Receivable | undefined> => {
    if (isNil(data)) return undefined

    const invoiceIsDueSoon = receivableInvoiceIsDueSoon(data)
    const invoiceIsOverdue = receivableInvoiceIsOverdue(data)

    if (isNil(data.attachments) || isEmpty(data.attachments) || !hasAttachments) {
        return { ...data, attachments: undefined, invoiceIsDueSoon, invoiceIsOverdue }
    }

    const attachments = await downloadAttachments(data.attachments)
    const attachmentCount = attachments?.length ?? 0
    return {
        ...data,
        attachments,
        attachmentCount,
        invoiceIsDueSoon,
        invoiceIsOverdue
    }
}

export const getReceivableByIdService = async (
    companyId: string,
    receivableId: string | undefined
): Promise<RequestResponse<Receivable> | undefined> => {
    if (!receivableId) {
        return undefined
    }

    const url = resolveApiUrl('receivables.get', {
        variables: { companyId, receivableId },
        query: {
            params: {
                include: 'attachmentUrl'
            }
        }
    })
    const response = await requestService<ReceivableGateway>(url)
    const mappedReceivable = await mapReceivableService(response?.body)

    return { ...response, body: mappedReceivable }
}

export const createReceivableService = async (
    companyId: string,
    payload: CreateReceivable
): Promise<RequestResponse<Receivable> | undefined> => {
    const url = resolveApiUrl('receivables.create', {
        variables: { companyId }
    })

    const files = await uploadFileService(payload.attachments, companyId)
    const sanitizedFiles = files?.filter((file): file is UploadFileGateway => !isNil(file))
    const mappedFiles = sanitizedFiles?.map((file) => ({
        id: file.fileId,
        url: file.url
    }))

    const formattedPayload: CreateReceivableGateway = {
        ...payload,
        attachments: mappedFiles
    }

    const response = await requestService<ReceivableGateway>(url, {
        method: 'POST',
        data: formattedPayload
    })

    trackEvent('Create_Receivable')

    const mappedReceivable = await mapReceivableService(response?.body)
    return { ...response, body: mappedReceivable }
}

export const updateReceivableService = async (
    companyId: string,
    payload: UpdateReceivable
): Promise<RequestResponse<Receivable> | undefined> => {
    const { receivableId, ...rest } = payload

    const url = resolveApiUrl('receivables.update', {
        variables: { companyId, receivableId }
    })

    const files = await uploadFileService(payload.attachments, companyId)
    const sanitizedFiles = files?.filter((file): file is UploadFileGateway => !isNil(file))
    const mappedFiles = sanitizedFiles?.map((file) => ({
        id: file.fileId,
        url: file.url
    }))

    const formattedPayload: UpdateReceivableGateway = {
        ...rest,
        attachments: mappedFiles
    }

    const response = await requestService<ReceivableGateway>(url, {
        method: 'PATCH',
        data: formattedPayload
    })

    trackEvent('Update_Receivable')

    const mappedReceivable = await mapReceivableService(response?.body)
    return { ...response, body: mappedReceivable }
}
